/**
 * useFormPayload - Transform form data to Change Address API request payload
 *
 * Converts camelCase form data into snake_case API format
 * matching Python API's ChangeAddressRequest Pydantic model
 */

// ===== Python API matching types =====

export interface ChangeAddressPayload {
  credential_id?: string;
  session_id: string;
  unique_entity_number: string;
  firm_name: string;
  country: string;
  state: string;
  city: string;
  post_code: string;
  street01: string;
  street02?: string;
  street03?: string;
  date_of_change: string;
  accessible_ordinary_business_hours: boolean;
  // EOT fields
  extension_of_time: boolean;
  eot_granted_date?: string;
  eot_attachment?: string; // base64 encoded file content
  eot_attachment_name?: string;
}

// ===== Helpers =====

const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

/**
 * Convert "DD MMMM YYYY" (e.g., "11 JULY 1983") to "YYYY-MM-DD" for Python API
 * Also accepts ISO format passthrough
 */
function toISODate(dateStr: string): string {
  if (!dateStr) return '';
  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // DD MMMM YYYY
  const match = dateStr.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (match) {
    const monthIdx = MONTH_NAMES.indexOf(match[2]);
    if (monthIdx >= 0) {
      const day = match[1].padStart(2, '0');
      const month = String(monthIdx + 1).padStart(2, '0');
      return `${match[3]}-${month}-${day}`;
    }
  }
  return dateStr;
}

/**
 * Build the Change Address API payload from form data
 */
export function buildPayload(
  formData: Record<string, any>,
  sessionId: string,
  credentialId?: string
): ChangeAddressPayload {
  // Sections (new structure: extensionOfTime, entityInfo, newAddress, changeDetails)
  const eotSection = formData.extensionOfTime || {};
  const entityInfo = formData.entityInfo || {};
  const changeDetails = formData.changeDetails || {};

  // Address from addressBlock
  const addresses = formData.newAddress?._addresses || {};
  const regAddr = addresses.reg || {};

  const isEot = !!eotSection.extensionOfTime;

  // EOT attachment: file field는 { name, content (base64), type } 형태
  const eotFile = eotSection.eotAttachment;

  return {
    credential_id: credentialId,
    session_id: sessionId,
    unique_entity_number: entityInfo.companyNumber || '',
    firm_name: entityInfo.firmName || '',
    country: regAddr.country || 'MALAYSIA',
    state: regAddr.state || '',
    city: regAddr.city || '',
    post_code: regAddr.postcode || regAddr.post_code || '',
    street01: regAddr.street01 || '',
    street02: regAddr.street02 || '',
    street03: regAddr.street03 || '',
    date_of_change: toISODate(changeDetails.dateOfChange || ''),
    accessible_ordinary_business_hours: changeDetails.accessibleOrdinaryBusinessHours !== false,
    // EOT
    extension_of_time: isEot,
    eot_granted_date: isEot ? toISODate(eotSection.grantedDate || '') : undefined,
    eot_attachment: isEot && eotFile?.content ? eotFile.content : undefined,
    eot_attachment_name: isEot && eotFile?.name ? eotFile.name : undefined,
  };
}

export function useFormPayload() {
  return { buildPayload };
}
